import { Auth } from 'aws-amplify';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Checkbox, Input, notification, Button, ApiMeResponse } from 'src/common';
import { Paths } from 'src/pages/paths';
import styles from '../index.module.scss';
import { usePasswordValidator } from 'src/helpers/passwords';
import { InfoLabel } from 'src/shared';
import { AuthUser, tempCognitoUserVar, useApp, UserType } from 'src/models/auth';
import { useNavigate } from 'react-router-dom';
import { StorageKey } from 'src/types/enums';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-light-svg-icons';
import { useLazyQuery } from '@apollo/client';
import { GET_ME_INFO } from 'src/models/auth/queries';
import { Ownerships, ownership } from 'src/models/ownership';

interface FormState {
  password: string;
  confirmPassword: string;
  acceptedCookie: boolean;
}

export const ChangePasswordPage: FC = () => {
  const { t } = useTranslation();
  const hideAcceptedCookie = ownership.name === Ownerships.Clira;
  const [acceptedCookie, setAcceptedCookie] = useState(hideAcceptedCookie);
  const { permission, loading, handlePaidUser, handleFreeUser } = useApp();
  const [loadPaidUser] = useLazyQuery<ApiMeResponse>(GET_ME_INFO);
  const navigate = useNavigate();
  const from = sessionStorage.getItem(StorageKey.FromLogin);
  const redirectTo = useMemo(
    () => (!!from ? from : permission === UserType.Free ? Paths.FREE_HOME_PATH : Paths.HOME_PATH),
    [from, permission]
  );
  const [form] = Form.useForm<FormState>();

  const initialValues = useMemo((): FormState => {
    return {
      password: '',
      confirmPassword: '',
      acceptedCookie: hideAcceptedCookie
    };
  }, [hideAcceptedCookie]);

  const onFinish = useCallback(
    async (values: FormState) => {
      const { password: newPassword } = values;
      const authUser = tempCognitoUserVar();
      const { requiredAttributes } = authUser?.challengeParam;
      const username = authUser?.getUsername();

      Auth.completeNewPassword(authUser, newPassword, requiredAttributes)
        .then(() => {
          username &&
            Auth.signIn(username, newPassword)
              .then(data => {
                const permission = (data as AuthUser).attributes?.['custom:userType'];
                if (permission !== UserType.Free) {
                  handlePaidUser(data).then(async () => {
                    const { data: meData } = await loadPaidUser();
                    const isCompany =
                      !!meData?.getMe.account?.company.orgNumber && !!meData?.getMe.account?.company.companyName;
                    sessionStorage.removeItem(StorageKey.FromLogin);
                    navigate(isCompany ? redirectTo : Paths.START, { replace: true });
                  });
                } else {
                  handleFreeUser(data);
                  sessionStorage.removeItem(StorageKey.FromLogin);
                  navigate(redirectTo, { replace: true });
                }
              })
              .catch(() => {
                notification.error({
                  message: t('Auth.notSignedIn'),
                  description: t('Common.unknownErrorDesc')
                });
              });
        })
        .catch(() => {
          notification.error({
            message: t('Auth.passwordNotUpdated'),
            description: t('Common.unknownErrorDesc')
          });
        });
    },
    [handleFreeUser, handlePaidUser, loadPaidUser, navigate, redirectTo, t]
  );

  const passwordRule = usePasswordValidator();

  return (
    <Form form={form} onFinish={onFinish} initialValues={initialValues} layout={'vertical'} className={styles.form}>
      <div className={styles.heading}>
        <h3 className={styles.title}>
          <span>{t('Auth.changePassword.title')}</span>
        </h3>
        <span className={styles.desc}>{t('Auth.changePassword.desc')}</span>
      </div>
      <Form.Item
        name="password"
        label={<InfoLabel title={t('Auth.preferredPassword')} desc={t('Auth.passwordDescription')} />}
        rules={[
          { required: true, message: t('Auth.enterNewPassword') },
          { validator: passwordRule, message: t('Auth.passwordPolicy') }
        ]}
      >
        <Input.Password
          placeholder={t('Auth.newPassword')}
          autoComplete={'new-password'}
          type={'password'}
          iconRender={visible => <FontAwesomeIcon icon={visible ? faEye : faEyeSlash} />}
        />
      </Form.Item>
      <Form.Item
        name="confirmPassword"
        label={t('Auth.againPassword')}
        dependencies={['password']}
        rules={[
          { required: true, message: t('Auth.enterNewPassword') },
          ({ getFieldValue }) => ({
            validator(rule, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(t('Auth.passwordMustBeTheSame'));
            }
          })
        ]}
      >
        <Input.Password
          placeholder={t('Auth.againPassword')}
          autoComplete={'new-password'}
          type={'password'}
          iconRender={visible => <FontAwesomeIcon icon={visible ? faEye : faEyeSlash} />}
        />
      </Form.Item>
      {!hideAcceptedCookie && (
        <Form.Item name="acceptedCookie" className={styles.agreePolicy}>
          <Checkbox onChange={e => setAcceptedCookie(e.target.checked)} checked={acceptedCookie}>
            <span className={styles.agreePolicyCheckbox}>
              {t('Auth.iHaveReadAndAccept')}{' '}
              {
                <a
                  className={styles.link}
                  target="_blank"
                  href="https://issuu.com/tendium/docs/tendeye_terms_of_use_pdf?fr=sODg3ZjEwNzAyMjU"
                  rel="noopener noreferrer"
                >
                  {t('Auth.termsAndConditionsText')}
                </a>
              }
              {', '}
              {
                <a
                  className={styles.link}
                  href="https://links.tendium.ai/cookie-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('Auth.cookiePolicyTitle')}
                </a>
              }{' '}
              {t('and')}{' '}
              {
                <a
                  className={styles.link}
                  href="https://links.tendium.ai/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('Auth.privacyPolicyTitle')}
                </a>
              }
            </span>
          </Checkbox>
        </Form.Item>
      )}
      <div className={styles.actions}>
        <Button
          htmlType={'submit'}
          type={'primary'}
          loading={loading}
          className={styles.button}
          disabled={!acceptedCookie}
        >
          {t('Auth.common.buttons.continue')}
        </Button>
      </div>
    </Form>
  );
};

export default ChangePasswordPage;
